import {
  MAX_ELIMINATIONS,
  MAX_FOOD,
  MAX_LENGTH,
  MAX_MATCHES,
  MAX_TOTAL,
  createDefaultProgress,
  serializedProgressBytes,
  validateProgress,
  type ProgressRecords,
} from "./schema";
import type { StorageAdapter } from "./local-store";

export const ENDLESS_JOURNAL_MAX_BYTES = 8 * 1024;
export const ASSISTED_TOMBSTONE_MAX_BYTES = 1024;
export const MATCH_PERSISTENCE_VERSION = 1 as const;

export type ProgressOwnerScope = "guest" | `uid:${string}`;
export type MatchPersistenceMode = "signal" | "endless";

export interface EndlessCheckpoint {
  sessionMs: number;
  aliveMs: number;
  currentLength: number;
  maxLength: number;
  eliminations: number;
  deaths: number;
  foodCollected: number;
}

export interface EndlessSessionJournal {
  version: typeof MATCH_PERSISTENCE_VERSION;
  sessionId: string;
  ownerScope: ProgressOwnerScope;
  baseRecords: ProgressRecords;
  checkpoint: EndlessCheckpoint;
  assisted: boolean;
  updatedAt: number;
}

export interface AssistedMatchTombstone {
  version: typeof MATCH_PERSISTENCE_VERSION;
  sessionId: string;
  ownerScope: ProgressOwnerScope;
  assisted: true;
  updatedAt: number;
}

export type MarkAssistedResult =
  | { ok: true }
  | {
      ok: false;
      quarantineRequired: boolean;
      reason:
        | "storage"
        | "readback"
        | "journal-missing"
        | "journal-mismatch"
        | "owner-mismatch";
    };

const JOURNAL_FIELDS = new Set([
  "version",
  "sessionId",
  "ownerScope",
  "baseRecords",
  "checkpoint",
  "assisted",
  "updatedAt",
]);
const TOMBSTONE_FIELDS = new Set([
  "version",
  "sessionId",
  "ownerScope",
  "assisted",
  "updatedAt",
]);
const CHECKPOINT_FIELDS = new Set([
  "sessionMs",
  "aliveMs",
  "currentLength",
  "maxLength",
  "eliminations",
  "deaths",
  "foodCollected",
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasExactFields = (
  value: Record<string, unknown>,
  fields: Set<string>,
): boolean => {
  const keys = Object.keys(value);
  return keys.length === fields.size && keys.every((key) => fields.has(key));
};

const boundedInteger = (value: unknown, max: number): value is number =>
  typeof value === "number" &&
  Number.isSafeInteger(value) &&
  value >= 0 &&
  value <= max;

const validSessionId = (value: unknown): value is string =>
  typeof value === "string" && /^[A-Za-z0-9_-]{1,128}$/.test(value);

export const isProgressOwnerScope = (
  value: unknown,
): value is ProgressOwnerScope =>
  value === "guest" ||
  (typeof value === "string" && /^uid:[A-Za-z0-9_-]{1,128}$/.test(value));

export const progressOwnerScope = (uid: string | null): ProgressOwnerScope => {
  if (uid === null) return "guest";
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(uid)) {
    throw new Error("invalid Firebase UID for match persistence");
  }
  return `uid:${uid}`;
};

const recordsAreValid = (value: unknown): value is ProgressRecords => {
  const candidate = createDefaultProgress(0);
  candidate.records = value as ProgressRecords;
  return validateProgress(candidate).ok;
};

const checkpointIsValid = (value: unknown): value is EndlessCheckpoint =>
  isRecord(value) &&
  hasExactFields(value, CHECKPOINT_FIELDS) &&
  boundedInteger(value.sessionMs, MAX_TOTAL) &&
  boundedInteger(value.aliveMs, MAX_TOTAL) &&
  value.aliveMs <= value.sessionMs &&
  boundedInteger(value.currentLength, MAX_LENGTH) &&
  boundedInteger(value.maxLength, MAX_LENGTH) &&
  value.currentLength <= value.maxLength &&
  boundedInteger(value.eliminations, MAX_ELIMINATIONS) &&
  boundedInteger(value.deaths, MAX_ELIMINATIONS) &&
  boundedInteger(value.foodCollected, MAX_FOOD);

export const validateEndlessJournal = (
  value: unknown,
): value is EndlessSessionJournal =>
  serializedProgressBytes(value) <= ENDLESS_JOURNAL_MAX_BYTES &&
  isRecord(value) &&
  hasExactFields(value, JOURNAL_FIELDS) &&
  value.version === MATCH_PERSISTENCE_VERSION &&
  validSessionId(value.sessionId) &&
  isProgressOwnerScope(value.ownerScope) &&
  recordsAreValid(value.baseRecords) &&
  checkpointIsValid(value.checkpoint) &&
  typeof value.assisted === "boolean" &&
  boundedInteger(value.updatedAt, Number.MAX_SAFE_INTEGER);

export const validateAssistedTombstone = (
  value: unknown,
): value is AssistedMatchTombstone =>
  serializedProgressBytes(value) <= ASSISTED_TOMBSTONE_MAX_BYTES &&
  isRecord(value) &&
  hasExactFields(value, TOMBSTONE_FIELDS) &&
  value.version === MATCH_PERSISTENCE_VERSION &&
  validSessionId(value.sessionId) &&
  isProgressOwnerScope(value.ownerScope) &&
  value.assisted === true &&
  boundedInteger(value.updatedAt, Number.MAX_SAFE_INTEGER);

const journalKey = (scope: ProgressOwnerScope): string =>
  `snake:endless-journal:${scope}:v1`;
const tombstoneKey = (scope: ProgressOwnerScope): string =>
  `snake:assisted-tombstone:${scope}:v1`;

const parse = (raw: string | null): unknown => {
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
};

const capAdd = (left: number, right: number, maximum: number): number =>
  Math.min(maximum, left + right);

export const recoverEndlessRecords = (
  journal: EndlessSessionJournal,
): ProgressRecords => {
  if (!validateEndlessJournal(journal) || journal.assisted) {
    throw new Error("only a valid unassisted journal can be recovered");
  }
  const records = { ...journal.baseRecords };
  const checkpoint = journal.checkpoint;
  records.endlessSessions = capAdd(records.endlessSessions, 1, MAX_MATCHES);
  records.endlessDeaths = capAdd(
    records.endlessDeaths,
    checkpoint.deaths,
    MAX_ELIMINATIONS,
  );
  records.longestEndlessSessionMs = Math.max(
    records.longestEndlessSessionMs,
    checkpoint.sessionMs,
  );
  records.longestEndlessLength = Math.max(
    records.longestEndlessLength,
    checkpoint.maxLength,
  );
  records.endlessEliminations = capAdd(
    records.endlessEliminations,
    checkpoint.eliminations,
    MAX_ELIMINATIONS,
  );
  records.endlessFoodCollected = capAdd(
    records.endlessFoodCollected,
    checkpoint.foodCollected,
    MAX_FOOD,
  );
  records.totalEndlessSessionMs = capAdd(
    records.totalEndlessSessionMs,
    checkpoint.sessionMs,
    MAX_TOTAL,
  );
  records.totalEndlessAliveMs = capAdd(
    records.totalEndlessAliveMs,
    checkpoint.aliveMs,
    MAX_TOTAL,
  );
  return records;
};

export class MatchPersistenceStore {
  constructor(
    private readonly storage: StorageAdapter,
    private readonly now: () => number = Date.now,
  ) {}

  readJournal(scope: ProgressOwnerScope): EndlessSessionJournal | null {
    try {
      const value = parse(this.storage.getItem(journalKey(scope)));
      return validateEndlessJournal(value) && value.ownerScope === scope
        ? structuredClone(value)
        : null;
    } catch {
      return null;
    }
  }

  writeJournal(journal: EndlessSessionJournal): boolean {
    if (!validateEndlessJournal(journal)) return false;
    try {
      const raw = JSON.stringify(journal);
      this.storage.setItem(journalKey(journal.ownerScope), raw);
      const readback = this.readJournal(journal.ownerScope);
      return (
        readback?.sessionId === journal.sessionId &&
        readback.updatedAt === journal.updatedAt &&
        readback.assisted === journal.assisted
      );
    } catch {
      return false;
    }
  }

  checkpoint(
    scope: ProgressOwnerScope,
    sessionId: string,
    checkpoint: EndlessCheckpoint,
  ): boolean {
    const journal = this.readJournal(scope);
    if (
      !journal ||
      journal.sessionId !== sessionId ||
      !checkpointIsValid(checkpoint)
    ) {
      return false;
    }
    return this.writeJournal({
      ...journal,
      checkpoint: { ...checkpoint },
      updatedAt: this.now(),
    });
  }

  readTombstone(scope: ProgressOwnerScope): AssistedMatchTombstone | null {
    try {
      const value = parse(this.storage.getItem(tombstoneKey(scope)));
      return validateAssistedTombstone(value) && value.ownerScope === scope
        ? { ...value }
        : null;
    } catch {
      return null;
    }
  }

  markAssisted(
    scope: ProgressOwnerScope,
    sessionId: string,
    mode: MatchPersistenceMode,
  ): MarkAssistedResult {
    const tombstone: AssistedMatchTombstone = {
      version: MATCH_PERSISTENCE_VERSION,
      sessionId,
      ownerScope: scope,
      assisted: true,
      updatedAt: this.now(),
    };
    try {
      this.storage.setItem(tombstoneKey(scope), JSON.stringify(tombstone));
    } catch {
      return { ok: false, quarantineRequired: false, reason: "storage" };
    }
    const readback = this.readTombstone(scope);
    if (
      !readback ||
      readback.sessionId !== sessionId ||
      readback.updatedAt !== tombstone.updatedAt
    ) {
      return { ok: false, quarantineRequired: true, reason: "readback" };
    }
    if (mode === "signal") return { ok: true };

    const journal = this.readJournal(scope);
    if (!journal) {
      return {
        ok: false,
        quarantineRequired: true,
        reason: "journal-missing",
      };
    }
    if (journal.sessionId !== sessionId) {
      return {
        ok: false,
        quarantineRequired: true,
        reason: "journal-mismatch",
      };
    }
    const marked = this.writeJournal({
      ...journal,
      assisted: true,
      updatedAt: this.now(),
    });
    const finalTombstone = this.readTombstone(scope);
    if (!marked || finalTombstone?.sessionId !== sessionId) {
      return { ok: false, quarantineRequired: true, reason: "readback" };
    }
    return { ok: true };
  }

  clearJournal(scope: ProgressOwnerScope, sessionId: string): boolean {
    const journal = this.readJournal(scope);
    if (!journal || journal.sessionId !== sessionId) return false;
    try {
      this.storage.removeItem(journalKey(scope));
      return this.storage.getItem(journalKey(scope)) === null;
    } catch {
      return false;
    }
  }

  clearTombstone(scope: ProgressOwnerScope, sessionId: string): boolean {
    const tombstone = this.readTombstone(scope);
    if (!tombstone || tombstone.sessionId !== sessionId) return false;
    try {
      this.storage.removeItem(tombstoneKey(scope));
      return this.storage.getItem(tombstoneKey(scope)) === null;
    } catch {
      return false;
    }
  }
}
